import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AsyncButton } from '@/components/common/AsyncButton';
import { useToast } from '@/components/common/toast';
import type { MatchedTransactionRow, StatementCommentDto } from '@/api/types/providerStatements';
import type { ProviderStatementConfig } from './config';
import { MatchedStatementsTable } from './MatchedStatementsTable';
import { StatementCommentsPanel } from './StatementCommentsPanel';
import { CommentConfirmDialog } from '../providerFunds/CommentConfirmDialog';

interface Props {
  config: ProviderStatementConfig;
  reloadKey: number;
  can: { unmatch: boolean };
  onReloadAll: () => void;
}

export function MatchedTab({ config, reloadKey, can, onReloadAll }: Props) {
  const toast = useToast();
  const { api, family, statementIdField } = config;

  const [selected, setSelected] = useState<MatchedTransactionRow | null>(null);
  const [unmatchOpen, setUnmatchOpen] = useState(false);
  const [panel, setPanel] = useState<{ comments: StatementCommentDto[]; loading: boolean } | null>(null);

  const matched = useQuery({
    queryKey: ['provider-statement', config.key, 'matched', reloadKey],
    queryFn: api.getMatchedTransactions,
  });

  // Sort desc by transaction date (legacy #matched-transactions-table default).
  const rows = useMemo(() => {
    const list = (matched.data ?? []) as MatchedTransactionRow[];
    return [...list].sort(
      (a, b) =>
        new Date(b.transaction.dateOfTransaction).getTime() -
        new Date(a.transaction.dateOfTransaction).getTime(),
    );
  }, [matched.data]);

  const unmatchMutation = useMutation({
    mutationFn: (comment: string) =>
      api.unmatchTransactions({
        ticket: selected!.transaction.ticket,
        connectorId: selected!.transaction.connectorId,
        comment,
      }),
    onSuccess: (res) => {
      toast.success(res.message);
      setUnmatchOpen(false);
      setSelected(null);
      onReloadAll();
    },
  });

  const commentsMutation = useMutation({
    mutationFn: (args: { ticket: number; statementId: string }) =>
      api.getMatchedComments(args.ticket, args.statementId),
  });

  function openComments(row: MatchedTransactionRow) {
    const sid = String((row.statement as unknown as Record<string, unknown>)[statementIdField] ?? '');
    setPanel({ comments: [], loading: true });
    commentsMutation.mutate(
      { ticket: row.transaction.ticket, statementId: sid },
      { onSuccess: (items) => setPanel({ comments: items, loading: false }) },
    );
  }

  const selectedKey = selected ? `${selected.transaction.ticket}|${selected.transaction.connectorId}` : null;

  return (
    <div>
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
        <p className="mb-0 text-muted small">Select a matched row to unmatch it.</p>
        {can.unmatch && (
          <AsyncButton
            className="btn btn-primary btn-sm"
            disabled={!selected}
            onClick={() => setUnmatchOpen(true)}
          >
            Unmatch
          </AsyncButton>
        )}
      </div>

      <MatchedStatementsTable
        rows={rows}
        family={family}
        loading={matched.isLoading}
        selectedKey={selectedKey}
        onSelect={setSelected}
        excelFileName={`${config.title} Matched Transactions`}
        rowActions={(row) => (
          <button
            type="button"
            className="btn btn-sm btn-link p-0 px-1"
            title="Audit trail"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              openComments(row);
            }}
          >
            <i className="fa fa-plus-circle" />
          </button>
        )}
      />

      {panel && (
        <StatementCommentsPanel
          title="Audit Trail"
          comments={panel.comments}
          loading={panel.loading}
          onClose={() => setPanel(null)}
        />
      )}

      <CommentConfirmDialog
        show={unmatchOpen}
        title="Are you sure?"
        body={
          <p className="mb-0">
            Unmatch this entry? All other entries matched with {selected?.transaction.ticket} will be
            unmatched as well.
          </p>
        }
        confirmLabel="Yes"
        busy={unmatchMutation.isPending}
        onConfirm={(comment) => unmatchMutation.mutate(comment)}
        onCancel={() => setUnmatchOpen(false)}
      />
    </div>
  );
}
